"use client";

import { GitHubRepository } from "@/types/github.type";

export interface GitHubRepositoryCardProps {
  repo: GitHubRepository;
  animationDelay?: number;
}

export function GitHubRepositoryCard({ repo, animationDelay = 0 }: GitHubRepositoryCardProps) {
  const openRepository = () => {
    if (typeof window !== "undefined") {
      window.open(repo.html_url, "_blank", "noopener,noreferrer");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openRepository();
    }
  };

  return (
    <div
      role="link"
      tabIndex={0}
      aria-label={`Open ${repo.name} repository on GitHub`}
      onClick={openRepository}
      onKeyDown={handleKeyDown}
      className="mc-panel p-6 hover:translate-y-[-3px] transition-transform duration-200 animate-slideInUp group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      style={{ animationDelay: `${animationDelay}ms` }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <h3 className="text-lg text-primary group-hover:underline transition-all duration-200">{repo.name}</h3>
        {repo.language && (
          <span className="inline-block text-xs mc-slot text-primary px-2 py-1 shrink-0 uppercase">
            {repo.language}
          </span>
        )}
      </div>

      {repo.description && <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{repo.description}</p>}

      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <span className="text-yellow-500">★</span>
          <span>{repo.stargazers_count}</span>
        </div>
        <div className="flex items-center gap-1">
          <span>🔀</span>
          <span>{repo.forks_count}</span>
        </div>
        {repo.homepage && (
          <a
            href={repo.homepage}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto text-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            🔗 Demo
          </a>
        )}
      </div>

      {repo.topics.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-4">
          {repo.topics.slice(0, 5).map((topic) => (
            <span key={topic} className="text-xs mc-slot text-muted-foreground px-2 py-1">
              {topic}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
